<!-- eslint-disable vue/no-v-html -->
<template>
  <div class="parts-splitter">
    <div
      v-for="group in groupedProducts"
      :key="`${group.title}-${group.subtitle}`"
    >
      <div :class="`kits-header`">
        <div>
          <p v-if="!group.leMode">
            {{ group.subtitle }}
          </p>
          <BaseHeading size="h1">
            {{ group.title }}
          </BaseHeading>
          <p
            v-if="group.leMode"
            v-html="group.subtitle"
          />
        </div>
      </div>

      <div class="clearfix kits-block-cont">
        <sg-link
          v-for="product in group.products"
          :key="product.sku"
          :to="product.link"
        >
          <div class="kits-block">
            <dynamic-img
              v-if="product.dynamicImg"
              :sku="product.sku"
              :view-angle="product.viewAngle || 2"
              :width="1200"
              :height="800"
              placeholder-ratio="3x2"
            />

            <sg-img
              v-else
              :src="product.img"
              :class="product.imgClass"
              placeholder-ratio="2x1"
            />

            <BaseHeading size="h2">
              {{ product.title }}
            </BaseHeading>
            <p v-if="product.subtitle">
              {{ product.subtitle }}
            </p>
            <template v-if="!isNaN(getProductPrice(product.sku))">
              <p v-if="product.fixedPrice">
                {{ $tcl(getProductPrice(product.sku)) }}
              </p>
              <p v-else>
                {{
                  $tr('From {amount}', {
                    amount: $tcl(getProductPrice(product.sku)),
                  })
                }}
              </p>
            </template>
          </div>
        </sg-link>
      </div>
    </div>
  </div>
</template>

<script>
import CustomisedProduct from '~/models/CustomisedProduct'

function resolveGroupedProducts (pageMode) {
  let groups = []

  if (pageMode === 'lenskits')
    groups.push({
      title: 'Lens Kits',
      subtitle: 'Sunglasses',
      products: [
        {
          title: 'Sierras™ Lens Kits',
          sku: 'sierraslrk-sle_Prose',
          part: 'sle',
          dynamicImg: true,
          link: '/shop/lenses/sierras',
        },
        {
          title: 'Classics³ Lens Kits',
          sku: 'classics3lrk-c3le_Pgreen',
          part: 'c3le',
          dynamicImg: true,
          link: '/shop/lenses/classics3',
        },
        {
          title: 'Renegades™ Lens Kits',
          sku: 'renegadeslrk-rgle_Pblue',
          part: 'rgle',
          dynamicImg: true,
          link: '/shop/lenses/renegades',
        },
        {
          title: 'Miras™ Lens Kits',
          sku: 'miraslrk-mle_Pgold',
          part: 'mle',
          dynamicImg: true,
          link: '/shop/lenses/miras',
        },
        {
          title: 'Tempests™ Lens Kits',
          sku: 'tempestslrk-tle_Pfire',
          part: 'tle',
          dynamicImg: true,
          link: '/shop/lenses/tempests',
        },
        {
          title: 'Tokas™ Lens Kits',
          sku: 'tokaslrk-tole_8chrome',
          part: 'tole',
          dynamicImg: true,
          link: '/shop/lenses/tokas',
        },
        {
          title: 'Zephyrs™ Lens Kits',
          sku: 'zephyrslrk-zle_8smoke',
          part: 'zle',
          dynamicImg: true,
          link: '/shop/lenses/zephyrs',
        },
        {
          title: 'Vulcans™ Lens Kits',
          sku: 'vulcanslrk-vcle_8green',
          part: 'vcle',
          dynamicImg: true,
          link: '/shop/lenses/vulcans',
        },
        {
          title: 'GTs™ Lens Kits',
          sku: 'gtslrk-gtle_8silverblue',
          part: 'gtle',
          dynamicImg: true,
          link: '/shop/lenses/gts',
        },
        {
          title: 'Airas™ Lens Kits',
          sku: 'airaslrk-ale_8fire',
          part: 'ale',
          dynamicImg: true,
          link: '/shop/lenses/airas',
        },
        {
          title: 'Ultras™ Lens Kits',
          sku: 'ultraslrk-ule_8gold',
          part: 'ule',
          dynamicImg: true,
          link: '/shop/lenses/ultras',
        },
        {
          title: 'Velans™ Lens Kits',
          sku: 'velanslrk-vele_8silverblue',
          part: 'vele',
          dynamicImg: true,
          link: '/shop/lenses/velans',
        },
      ],
    })

  if (pageMode === 'cases')
    groups.push({
      title: 'Capsule Cases',
      subtitle: 'Sunglasses & Goggles',
      products: [
        {
          title: 'Sunglasses Capsule Case',
          subtitle:
            'For: Classics³, Renegades™, Sierras™, Tempests™, Zephyrs™ & Tokas™',
          sku: 'case-ct_capsule',
          dynamicImg: true,
          link: '/shop/cases/capsule?id=case-ct_capsule',
          fixedPrice: true,
        },
        {
          title: 'Pace Series™ Capsule Case',
          subtitle: 'For: Vulcans™, Airas™ Ultras™, Velans™ & GTs™',
          sku: 'case-ct_pacecapsule',
          dynamicImg: true,
          link: '/shop/cases/pacecapsule?id=case-ct_pacecapsule',
          fixedPrice: true,
        },
        {
          title: 'Snow Series™ Capsule Case',
          subtitle: 'For: Snipers™, Ullrs™ & Vanguards™',
          sku: 'case-ct_revoltcapsule',
          dynamicImg: true,
          link: '/shop/cases/snowcapsule?id=case-ct_revoltcapsule',
          fixedPrice: true,
        },
        {
          title: 'Vanguards™ and Ullrs™ Compact Case',
          subtitle: 'For: Vanguards™ and Ullrs™',
          sku: 'case-ct_compactvanguards',
          dynamicImg: true,
          link: '/shop/cases/snowcapsule?id=case-ct_compactvanguards',
          fixedPrice: true,
        },
        {
          title: 'Snipers™ Compact Case',
          subtitle: 'For: Snipers™',
          sku: 'case-ct_compactsnipers',
          dynamicImg: true,
          link: '/shop/cases/snowcapsule?id=case-ct_compactsnipers',
          fixedPrice: true,
        },
      ],
    })

  if (pageMode === 'goggleparts' || pageMode === 'lenskits') {
    groups = [
      ...groups,
      {
        title: 'Lens & Replacement Parts',
        subtitle: 'Goggles',
        products: [
          {
            title: 'Vanguards™ Lens Kits',
            dynamicImg: true,
            viewAngle: 5,
            sku: 'vanguardsparts-vf_none-vle_greenN-vs_none',
            part: 'vle',
            link: '/shop/parts/vanguards?id=vanguardsparts-vf_none-vle_greenN-vs_none',
          },
          {
            title: 'Vanguards™ Frames',
            dynamicImg: true,
            viewAngle: 5,
            sku: 'vanguardsparts-vf_blackM-vle_none-vs_none',
            part: 'vf',
            link: '/shop/parts/vanguards?id=vanguardsparts-vf_blackM-vle_none-vs_none',
          },
          {
            title: 'Vanguards™ Straps',
            dynamicImg: true,
            viewAngle: 5,
            sku: 'vanguardsparts-vf_none-vle_none-vs_blackwhiteS',
            part: 'vs',
            link: '/shop/parts/vanguards?id=vanguardsparts-vf_none-vle_none-vs_blackwhiteS',
          },
          {
            title: 'Ullrs™ Lens Kits',
            dynamicImg: true,
            viewAngle: 5,
            sku: 'ullrsparts-ulf_none-ulle_silverblueN-uls_none',
            part: 'ulle',
            link: '/shop/parts/ullrs?id=ullrsparts-ulf_none-ulle_silverblueN-uls_none',
          },
          {
            title: 'Ullrs™ Frames',
            dynamicImg: true,
            viewAngle: 5,
            sku: 'ullrsparts-ulf_whiteM-ulle_none-uls_none',
            part: 'ulf',
            link: '/shop/parts/ullrs?id=ullrsparts-ulf_whiteM-ulle_none-uls_none',
          },
          {
            title: 'Ullrs™ Straps',
            dynamicImg: true,
            viewAngle: 5,
            sku: 'ullrsparts-ulf_none-ulle_none-uls_whiteblackS',
            part: 'uls',
            link: '/shop/parts/ullrs?id=ullrsparts-ulf_none-ulle_none-uls_whiteblackS',
          },
          {
            title: 'Snipers™ Lens Kits',
            dynamicImg: true,
            viewAngle: 5,
            sku: 'snipersparts-snf_none-snle_pinkN-sns_none',
            part: 'snle',
            link: '/shop/parts/snipers?id=snipersparts-snf_none-snle_pinkN-sns_none',
          },
          {
            title: 'Snipers™ Frames',
            dynamicImg: true,
            viewAngle: 5,
            sku: 'snipersparts-snf_blackM-snle_none-sns_none',
            part: 'snf',
            link: '/shop/parts/snipers?id=snipersparts-snf_blackM-snle_none-sns_none',
          },
          {
            title: 'Snipers™ Straps',
            dynamicImg: true,
            viewAngle: 5,
            sku: 'snipersparts-snf_none-snle_none-sns_blackwhiteS',
            part: 'sns',
            link: '/shop/parts/snipers?id=snipersparts-snf_none-snle_none-sns_blackwhiteS',
          },
          {
            title: 'Revolts™ Lens Kits',
            dynamicImg: true,
            viewAngle: 5,
            sku: 'revoltparts-rf_none-rle_greenN-rs_none',
            part: 'rle',
            link: '/shop/parts/revolts?id=revoltparts-rf_none-rle_greenN-rs_none',
          },
          {
            title: 'Revolts™ Frames',
            dynamicImg: true,
            viewAngle: 5,
            sku: 'revoltparts-rf_blackM-rle_none-rs_none',
            part: 'rf',
            link: '/shop/parts/revolts?id=revoltparts-rf_blackM-rle_none-rs_none',
          },
          {
            title: 'Revolts™ Straps',
            dynamicImg: true,
            viewAngle: 5,
            sku: 'revoltparts-rf_none-rle_none-rs_blackwhiteT',
            part: 'rs',
            link: '/shop/parts/revolts?id=revoltparts-rf_none-rle_none-rs_blackwhiteT',
          },
        ],
      },
      {
        title: 'Frame Kits',
        subtitle: 'Pace Series™',
        products: [
          {
            title: 'Vulcans™ TF Frame Kit',
            subtitle: 'For: Vulcans™ TF & Vulcans™ FF',
            sku: 'vulcansck-vcn_Mblack',
            part: 'vcn',
            dynamicImg: true,
            fixedPrice: true,
            link: '/shop/parts/vulcans?id=vulcansck-vcn_Mblack',
          },
          {
            title: 'Vulcans FF Frame Kit',
            subtitle: 'For: Vulcans™ TF & Vulcans™ FF',
            sku: 'vulcansck-vcb_Mblack',
            part: 'vcb',
            dynamicImg: true,
            fixedPrice: true,
            link: '/shop/parts/vulcans?id=vulcansck-vcb_Mblack',
          },
          {
            title: 'Velans™ TF Frame Kit',
            subtitle: 'For: Velans™ TF & Velans™ FF',
            sku: 'velansck-ven_Mblack',
            part: 'ven',
            dynamicImg: true,
            fixedPrice: true,
            link: '/shop/parts/velans?id=velansck-ven_Mblack',
          },
          {
            title: 'Velans™ FF Frame Kit',
            subtitle: 'For: Velans™ TF & Velans™ FF',
            sku: 'velansck-veb_Mblack',
            part: 'veb',
            dynamicImg: true,
            fixedPrice: true,
            link: '/shop/parts/velans?id=velansck-veb_Mblack',
          },
          {
            title: 'Airas™ ZF Frame Kit',
            subtitle: 'For: Airas™ ZF & Airas™ BF',
            sku: 'airasck-an_Mblack',
            part: 'an',
            dynamicImg: true,
            fixedPrice: true,
            link: '/shop/parts/airas?id=airasck-an_Mblack',
          },
          {
            title: 'Airas™ BF Frame Kit',
            subtitle: 'For: Airas™ ZF & Airas™ BF',
            sku: 'airasck-ab_Mblack',
            part: 'ab',
            dynamicImg: true,
            fixedPrice: true,
            link: '/shop/parts/airas?id=airasck-ab_Mblack',
          },
        ],
      },
    ]
  }

  if (pageMode === 'paceparts')
    groups = [
      ...groups,
      {
        title: 'Frame Kits',
        subtitle: 'Pace Series™',
        products: [
          {
            title: 'Vulcans™ TF Frame Kit',
            subtitle: 'For: Vulcans™ TF & Vulcans™ FF',
            sku: 'vulcansck-vcn_Mblack',
            part: 'vcn',
            dynamicImg: true,
            fixedPrice: true,
            link: '/shop/parts/vulcans?id=vulcansck-vcn_Mblack',
          },
          {
            title: 'Vulcans FF Frame Kit',
            subtitle: 'For: Vulcans™ TF & Vulcans™ FF',
            sku: 'vulcansck-vcb_Mblack',
            part: 'vcb',
            dynamicImg: true,
            fixedPrice: true,
            link: '/shop/parts/vulcans?id=vulcansck-vcb_Mblack',
          },
          {
            title: 'Velans™ TF Frame Kit',
            subtitle: 'For: Velans™ TF & Velans™ FF',
            sku: 'velansck-ven_Mblack',
            part: 'ven',
            dynamicImg: true,
            fixedPrice: true,
            link: '/shop/parts/velans?id=velansck-ven_Mblack',
          },
          {
            title: 'Velans™ FF Frame Kit',
            subtitle: 'For: Velans™ TF & Velans™ FF',
            sku: 'velansck-veb_Mblack',
            part: 'veb',
            dynamicImg: true,
            fixedPrice: true,
            link: '/shop/parts/velans?id=velansck-veb_Mblack',
          },
          {
            title: 'Airas™ ZF Frame Kit',
            subtitle: 'For: Airas™ ZF & Airas™ BF',
            sku: 'airasck-an_Mblack',
            part: 'an',
            dynamicImg: true,
            fixedPrice: true,
            link: '/shop/parts/airas?id=airasck-an_Mblack',
          },
          {
            title: 'Airas™ BF Frame Kit',
            subtitle: 'For: Airas™ ZF & Airas™ BF',
            sku: 'airasck-ab_Mblack',
            part: 'ab',
            dynamicImg: true,
            fixedPrice: true,
            link: '/shop/parts/airas?id=airasck-ab_Mblack',
          },
        ],
      },
      {
        title: 'Lens Kits',
        subtitle: 'Pace Series™',
        products: [
          {
            title: 'Vulcans™ Lens Kits',
            subtitle: 'For: Vulcans™ TF & Vulcans™ FF',
            sku: 'vulcanslrk-vcle_8fire',
            part: 'vcle',
            dynamicImg: true,
            link: '/shop/lenses/vulcans',
          },
          {
            title: 'Velans™ Lens Kits',
            subtitle: 'For: Velans™ TF & Velans™ FF',
            sku: 'velanslrk-vele_8fire',
            part: 'vele',
            dynamicImg: true,
            link: '/shop/lenses/velans',
          },
          {
            title: 'Airas™ Lens Kits',
            subtitle: 'For: Airas™ TF & Airas™ FF',
            sku: 'airaslrk-ale_8fire',
            part: 'ale',
            dynamicImg: true,
            link: '/shop/lenses/airas',
          },
          {
            title: 'GTs™ Lens Kits',
            subtitle: 'For: GTs™',
            sku: 'gtslrk-gtle_8silverblue',
            part: 'gtle',
            dynamicImg: true,
            link: '/shop/lenses/gts',
          },
        ],
      },
    ]

  return groups
}

function getPageDescription (pageMode) {
  return (
    {
      lenskits:
        'Shop replacement 8KO® lenses for your SunGod sunglasses & goggles to optimise visual clarity for every adventure. Order online now.',
      cases:
        'Keep your sunglasses & goggles safe with ultra-durable cases constructed from reinforced nylon. Order online now.',
      goggleparts:
        'Shop replacement parts for your SunGod sunglasses & goggles. Lenses, frames, & goggle straps. Built to enhance your adventures. Order online now.',
      paceparts:
        'Shop frame kits for our Pace Series™ performance sunglasses - lenses & frames. Get the most out of your running or cycling. Order online now.',
    }[pageMode] ||
    'Shop replacement parts for your SunGod sunglasses & goggles. Lenses, frames, & goggle straps. Built to enhance your adventures. Order online now.'
  )
}

function getPageTitle (pageMode) {
  return (
    {
      lenskits: 'Replacement Lenses',
      cases: 'Sunglasses & Goggle Cases',
      goggleparts: 'Replacement Parts',
      paceparts: 'Pace Series™ Parts',
    }[pageMode] || 'Replacement Parts'
  )
}

export default defineNuxtComponent({
  props: {
    pageMode: {
      type: String,
      default: 'lenskits',
    }
  },
  async setup (props) {
    const { $product } = useNuxtApp()
    const { getTitle, getDescription } = useMetaHelper()

    const pageMode = props.pageMode || 'lenskits'

    const groupedProducts = resolveGroupedProducts(pageMode)
    const products = groupedProducts.map((group) => group.products).flat()
    const skus = products.map((product) => product.sku)
    const baseSKUs = [...new Set(skus.map((sku) => sku.split('-')[0]))]

    // Fetch product data
    await $product.get(baseSKUs)

    const pageTitle = getTitle(getPageTitle(pageMode))
    const pageDescription = getDescription(getPageDescription(pageMode))
    useSeoMeta({
      title: pageTitle,
      ogTitle: pageTitle,
      description: pageDescription,
      ogDescription: pageDescription
    })

    const customisedProducts = ref({})
    const productPrices = ref({})
    
    async function getCustomisedProduct (sku) {
      if (!(sku in customisedProducts.value)) {
        customisedProducts.value[sku] = new CustomisedProduct(
          await $product.get(sku.split('-')[0]),
          {},
          { sku }
        )
      }

      return customisedProducts.value[sku]
    }

    function getProductPrice(sku) {
      return productPrices.value[sku]
    }

    async function storeProductPrices(product) {
      const customisedProduct = await getCustomisedProduct(product.sku)
      const minPrice = customisedProduct.getMinimumPartPrice(product.part)
      productPrices.value[product.sku] = minPrice
    }

    // Calculate prices for each SKU
    await Promise.all(products.map(storeProductPrices))

    return {
      customisedProducts,
      productPrices,
      getProductPrice,
      groupedProducts
    }
  }
})
</script>

<style lang="postcss">
.parts-splitter {
  margin-bottom: 100px;

  .kits-header {
    @apply bg-grey-dark;
    width: 100%;
    position: relative;
    overflow: hidden;

    div {
      @apply text-white;
      padding: 60px 30px;

      p {
        margin: 0;
        font-weight: 200;
        text-transform: uppercase;
        font-size: 0.85em;
      }

      h1 {
        margin: 0;
        font-weight: 600;
      }
    }
  }

  .kits-block-cont {
    @apply border-[3px] border-white;

    .kits-block {
      @apply bg-grey-light border-[3px] border-white;
      width: 100%;
      text-align: center;
      box-sizing: border-box;
      overflow: hidden;
      border-radius: 10px;

      h3,
      p {
        position: relative;
        z-index: 2;
        text-transform: capitalize;
      }

      p {
        margin: 15px 0;
      }

      img {
        width: 100%;
        padding: 20px 0;
        box-sizing: border-box;

        &.vanguards,
        &.ullrs {
          width: 160%;
          position: relative;
          left: -35%;
        }
      }

      p {
        font-weight: 200;
      }
    }
  }

  @screen xl {
    .kits-block-cont .kits-block {
      width: 50%;
      float: left;
    }
  }

  @screen xxl {
    .kits-block-cont .kits-block {
      width: 33.33%;
      float: left;
    }
  }
}
</style>
